import { useCallback, useEffect, useRef, useState } from 'react';
import { api, AdminReadingLink, ReadingQueue } from '../api';
import { Icon } from '../Icon';

/**
 * Further-reading review.
 *
 * Links are drafted by research tooling and reach learners only once an editor
 * accepts them (Principle IV). The bulk action exists because a thousand
 * one-by-one clicks would be a rubber stamp anyway; spot-check a topic, then
 * accept it, and the audit trail names you.
 *
 * Conflicts — where the researcher found a trusted page that seems to support
 * a different option — cannot be accepted, here or in the database. They are a
 * question about the answer key, which goes through the dispute process.
 */
export function AdminReading() {
  const [state, setState] = useState<'draft' | 'accepted' | 'rejected'>('draft');
  const [topicId, setTopicId] = useState('');
  const [queue, setQueue] = useState<ReadingQueue | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmTopic, setConfirmTopic] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // The button pressed disappears (Confirm replaces Accept; a reload replaces
  // the list), so focus is moved on purpose rather than dropped on <body>.
  const confirmRef = useRef<HTMLButtonElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (confirmTopic) confirmRef.current?.focus(); }, [confirmTopic]);

  const load = useCallback(() => {
    const q = new URLSearchParams({ state });
    if (topicId) q.set('topicId', topicId);
    api.get<ReadingQueue>(`/admin/reading?${q}`).then(setQueue).catch((e) => setErr(e.message));
  }, [state, topicId]);
  useEffect(load, [load]);

  async function run(key: string, call: () => Promise<{ changed: number; refusedConflicts?: number }>, verb: string) {
    setBusy(key); setErr(null); setMsg(null);
    try {
      const out = await call();
      setMsg(`${out.changed} link${out.changed === 1 ? '' : 's'} ${verb}.` +
             (out.refusedConflicts ? ` ${out.refusedConflicts} conflict(s) left for a key decision.` : ''));
      setConfirmTopic(null);
      load();
    } catch (e: any) { setErr(e.message); } finally {
      setBusy(null);
      statusRef.current?.focus();
    }
  }

  const review = (l: AdminReadingLink, accept: boolean) =>
    run(l.id, () => api.post('/admin/reading/review', { ids: [l.id], accept }), accept ? 'accepted' : 'rejected');
  // Sends what the editor was shown; the server refuses if the topic has
  // changed since, so nothing unseen is accepted under their name.
  const acceptTopic = (id: string, expected: number) =>
    run(`topic-${id}`, () => api.post('/admin/reading/accept-topic',
      { topicId: id, expected, asOf: queue?.asOf }), 'accepted');

  return (
    <section aria-labelledby="reading-heading">
      <h2 id="reading-heading">Further reading</h2>
      <p className="muted">
        Links drafted by research tooling to pages that explain why each keyed answer is right.
        Nothing here is shown to learners until you accept it. Open a sample of each topic before
        accepting it as a whole.
      </p>

      <div aria-live="polite" ref={statusRef} tabIndex={-1}>
        {msg && <p className="notice" role="status">{msg}</p>}
        {err && <p className="notice notice--warn" role="alert">{err}</p>}
      </div>

      {queue && queue.topics.length > 0 && (
        <div className="scroll-x">
          <table>
            <caption className="visually-hidden">Further reading by topic</caption>
            <thead>
              <tr>
                <th scope="col">Topic</th><th scope="col">Drafts</th>
                <th scope="col">Conflicts</th><th scope="col">Accepted</th><th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {queue.topics.map((t) => (
                <tr key={t.topicId}>
                  <th scope="row" style={{ fontWeight: 400 }}>
                    {t.topicName} <span className="muted">· {t.subject}</span>
                  </th>
                  <td>{t.drafts}</td><td>{t.conflicts}</td><td>{t.accepted}</td>
                  <td>
                    {t.drafts === 0 ? <span className="muted">—</span>
                      : confirmTopic === t.topicId ? (
                        <span className="btn-row" style={{ margin: 0 }}>
                          <button ref={confirmRef} className="btn-primary" disabled={busy === `topic-${t.topicId}`}
                                  onClick={() => void acceptTopic(t.topicId, t.drafts)}>
                            Confirm: accept {t.drafts}
                            <span className="visually-hidden"> links in {t.topicName}</span>
                          </button>
                          <button onClick={() => setConfirmTopic(null)}>Cancel</button>
                        </span>
                      ) : (
                        <button onClick={() => setConfirmTopic(t.topicId)}>
                          Accept all drafts<span className="visually-hidden"> in {t.topicName}</span>
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="btn-row" style={{ alignItems: 'flex-end' }}>
        <div>
          <label htmlFor="reading-state">Show</label>
          <select id="reading-state" value={state} onChange={(e) => setState(e.target.value as typeof state)}>
            <option value="draft">Waiting for review</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div>
          <label htmlFor="reading-topic">Topic</label>
          <select id="reading-topic" value={topicId} onChange={(e) => setTopicId(e.target.value)}>
            <option value="">All topics</option>
            {queue?.topics.map((t) => <option key={t.topicId} value={t.topicId}>{t.topicName}</option>)}
          </select>
        </div>
      </div>

      {queue && queue.links.length === 0 && <p className="muted">Nothing to show.</p>}
      {queue && queue.links.length === 500 && (
        <p className="muted">Showing the first 500. Choose a topic to narrow the list.</p>
      )}

      <ul className="submission-list">
        {queue?.links.map((l) => (
          <li key={l.id} className="card">
            <p style={{ margin: '0 0 0.25rem' }}>
              <span className="muted">{l.externalId} · {l.topicNames.join(', ')}</span>{' '}
              {l.alignment === 'conflict' && (
                <span className="pill pill--bad">Source may disagree with the key</span>
              )}
            </p>
            <p style={{ margin: '0 0 0.5rem' }}>{l.stem}</p>
            <p style={{ margin: '0 0 0.5rem' }}>
              Keyed answer: <strong>{l.correctResponse.join(', ').toUpperCase()}</strong>{' '}
              {l.correctResponse.length === 1 && `— ${l.options.find((o) => o.key === l.correctResponse[0])?.text ?? ''}`}
            </p>
            <p style={{ margin: '0 0 0.5rem' }}>
              <a href={l.url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">
                {l.title}<Icon name="external-link" />
                <span className="visually-hidden"> (opens in a new tab)</span>
              </a>
              <span className="muted"> — {l.publisher}</span>
            </p>
            <p className="muted" style={{ margin: '0 0 0.5rem', wordBreak: 'break-all' }}>{l.url}</p>
            {l.supports && <p style={{ margin: '0 0 0.5rem' }}><strong>What the page says:</strong> {l.supports}</p>}
            {l.note && <p className="notice" style={{ margin: '0 0 0.5rem' }}><strong>Researcher's note:</strong> {l.note}</p>}
            <p className="muted" style={{ margin: 0 }}>
              Drafted by {l.draftedBy}{l.accessedOn ? `, checked ${l.accessedOn.slice(0, 10)}` : ''}
              {l.reviewerName && ` · ${l.state} by ${l.reviewerName}`}
            </p>
            {l.state === 'draft' && (
              <div className="btn-row" style={{ marginTop: '0.6rem' }}>
                {l.alignment === 'aligned' ? (
                  <button className="btn-primary" disabled={busy === l.id} onClick={() => void review(l, true)}>
                    Accept link<span className="visually-hidden"> for {l.externalId}</span>
                  </button>
                ) : (
                  <span className="muted">
                    Cannot be accepted. If the key looks wrong, raise it in reported answer keys.
                  </span>
                )}
                <button disabled={busy === l.id} onClick={() => void review(l, false)}>
                  Reject link<span className="visually-hidden"> for {l.externalId}</span>
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
